import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, FormControlLabel, FormGroup, Stack, Typography, useTheme } from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { z } from 'zod';

import ErrorMessage from '@components/ErrorMessage';
import Loader from '@components/Loader';
import { RefetchingIndicator } from '@components/RefetchingIndicator';

import { avatarConfigurationTexts } from '../constants';
import { useAvatarSupportedLanguages } from '../services/queries';
import { AvatarSupportedLanguages } from '../services/types';
import { MenuOptionValue } from '../types';

const avatarLanguagesSchema = z
    .record(z.string(), z.boolean())
    .refine(
        (languages) =>
            Object.values(languages).some((isRecognizedByAvatar) => isRecognizedByAvatar),
        {
            message: avatarConfigurationTexts.selectLanguagesError,
        }
    );

type AvatarFormData = z.infer<typeof avatarLanguagesSchema>;

type AvatarFormProps = {
    data: AvatarSupportedLanguages;
    mutate: UseMutateFunction<void, Error, AvatarSupportedLanguages>;
    isSending: boolean;
};

const AvatarForm = ({ data, mutate, isSending }: AvatarFormProps) => {
    const theme = useTheme();

    const defaultValues = data.reduce<AvatarFormData>((acc, language) => {
        acc[language.id] = language.isRecognizedByAvatar;
        return acc;
    }, {});

    const {
        control,
        handleSubmit,
        trigger,
        formState: { isValid, errors },
    } = useForm<AvatarFormData>({
        resolver: zodResolver(avatarLanguagesSchema),
        mode: 'all',
        defaultValues,
    });

    const onSubmit = (formData: AvatarFormData) => {
        const updatedLanguages = data.map((language) => ({
            ...language,
            isRecognizedByAvatar: formData[language.id],
        }));
        mutate(updatedLanguages);
    };

    const hasErrors = !isValid && Object.keys(errors).length > 0;

    return (
        <Stack
            component='form'
            id={MenuOptionValue.AvatarConfiguration}
            onSubmit={handleSubmit(onSubmit)}
            gap={2}
        >
            <Typography variant='body1'>{avatarConfigurationTexts.selectLanguagesLabel}</Typography>
            <FormGroup>
                {data.map(({ id, language }) => (
                    <Controller
                        key={id}
                        name={id}
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...field}
                                        checked={field.value}
                                        disabled={isSending}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            trigger();
                                        }}
                                        sx={{
                                            color: theme.palette.customColors.purple,
                                            '&.Mui-checked': {
                                                color: theme.palette.customColors.purple,
                                            },
                                        }}
                                    />
                                }
                                label={language}
                            />
                        )}
                    />
                ))}
            </FormGroup>
            {hasErrors && (
                <Typography variant='body2' color='error'>
                    {avatarConfigurationTexts.selectLanguagesError}
                </Typography>
            )}
        </Stack>
    );
};

type AvatarConfigurationProps = {
    mutate: UseMutateFunction<void, Error, AvatarSupportedLanguages>;
    isSending: boolean;
};

const AvatarConfiguration = ({ mutate, isSending }: AvatarConfigurationProps) => {
    const { data, isLoading, isFetching, error, isError } = useAvatarSupportedLanguages();

    if (isLoading) return <Loader />;

    if (isError) return <ErrorMessage error={error} />;

    if (!data) return null;

    if (data.length === 0)
        return <Typography variant='body2'>{avatarConfigurationTexts.noLanguagesFound}</Typography>;

    const isRefetching = !isLoading && isFetching;

    return (
        <>
            {isRefetching && <RefetchingIndicator />}
            <AvatarForm data={data} mutate={mutate} isSending={isSending} />
        </>
    );
};

export default AvatarConfiguration;
